/**
 * Per-user calendar watch preferences (client ⇄ server contract).
 *
 * A connected Google account exposes many calendars (primary + secondary/shared). By default Lapo
 * treats the "obvious" ones as active — the primary, calendars the user owns/can write, and any the
 * user has made visible in Google Calendar. This model lets the user override that per calendar:
 * disable one that's on by default, or enable one that's off. A disabled calendar is turned off
 * EVERYWHERE — it is neither watched for automations nor shown in the agenda/brief/day view.
 *
 * Storage is sparse: only calendars the user has explicitly toggled appear in `overrides`; everything
 * else follows the server-computed default. Keys are `${accountId}::${calendarId}` so the same
 * `primary` id under two linked accounts stays distinct.
 */
export interface CalendarPrefs {
  /** explicit per-calendar overrides; key = calendarPrefKey(accountId, calendarId), value = watched? */
  overrides: Record<string, boolean>;
}

/** Stable key for a calendar within an account (calendarId alone isn't unique — `primary` repeats). */
export function calendarPrefKey(accountId: string, calendarId: string): string {
  return `${accountId}::${calendarId}`;
}

export function defaultCalendarPrefs(): CalendarPrefs {
  return { overrides: {} };
}

/** Whether a calendar is active, given the user's override (if any) or the server's default policy. */
export function isCalendarWatched(prefs: CalendarPrefs, key: string, defaultOn: boolean): boolean {
  const override = prefs.overrides[key];
  return typeof override === 'boolean' ? override : defaultOn;
}

/** Coerce untrusted input (a PUT body) into a valid CalendarPrefs — drop non-boolean override values. */
export function coerceCalendarPrefs(input: unknown): CalendarPrefs {
  if (!input || typeof input !== 'object') return defaultCalendarPrefs();
  const raw = (input as Record<string, unknown>).overrides;
  if (!raw || typeof raw !== 'object') return defaultCalendarPrefs();
  const overrides: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'boolean' && k) overrides[k] = v;
  }
  return { overrides };
}
